// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { canAddAllToList as getCanAddAllToList } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import OrderActionSet, {
    orderActionSetFieldDefinitions,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useCartState } from "@toolkit/Framework/Store/Data/Carts/CartsHooks";
import { useHomePageUrl } from "@toolkit/Framework/Store/Links/LinksHooks";
import { useOnePageCheckoutConfirmation } from "@toolkit/Framework/Store/Toolkit/Pages/OnePageCheckout/OnePageCheckoutHooks";
import React, { FC } from "react";

const enum fields {
    showPrint = "showPrint",
    showContinueShopping = "showContinueShopping",
    showAddToList = "showAddToList",
}

export interface OnePageCheckoutConfirmationActionsProps extends WidgetProps {
    fields: {
        [fields.showPrint]: boolean;
        [fields.showContinueShopping]: boolean;
        [fields.showAddToList]: boolean;
    };
}

export interface OnePageCheckoutConfirmationActionsStyles {
    printButton?: ButtonPresentationProps;
    printLink?: LinkPresentationProps;
    continueButton?: ButtonPresentationProps;
    continueLink?: LinkPresentationProps;
    addToListButton?: ButtonPresentationProps;
    addToListLink?: LinkPresentationProps;
}

export const onePageCheckoutConfirmationActionsStyles: OnePageCheckoutConfirmationActionsStyles = {
    addToListButton: { variant: "tertiary" },
};

const styles = onePageCheckoutConfirmationActionsStyles;

const OnePageCheckoutConfirmationActions: FC<OnePageCheckoutConfirmationActionsProps> = ({
    fields: widgetFields,
}: OnePageCheckoutConfirmationActionsProps) => {
    const dispatch = useAppDispatch();
    const { cartId } = useOnePageCheckoutConfirmation();
    const history = useHistory();
    const toasterContext = useToaster();
    const homePageUrl = useHomePageUrl();
    const { wishListSettings } = useSettingsCollection();
    const cartState = useCartState(cartId).value;
    const canAddAllToList = getCanAddAllToList(cartState);

    const continueClickHandler = () => {
        history.push(homePageUrl);
    };

    const addToListHandler = () => {
        const productInfos = cartState?.cartLines?.map(productInfo => ({
            productId: productInfo.productId,
            qtyOrdered: productInfo.qtyOrdered,
            unitOfMeasure: productInfo.unitOfMeasure,
        })) as Omit<ProductInfo, "productDetailPath">[];

        if (!wishListSettings.allowMultipleWishLists) {
            dispatch(
                addToWishList({
                    productInfos,
                    onSuccess: () => {
                        toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                    },
                }),
            );
            return;
        }

        dispatch(setAddToListModalIsOpen({ modalIsOpen: true, productInfos }));
    };

    return (
        <OrderActionSet
            fields={widgetFields}
            actions={[
                {
                    preset: "print",
                    buttonStyles: styles.printButton,
                    linkStyles: styles.printLink,
                    field: fields.showPrint,
                    testSelector: "orderConfirmation_printButton",
                },
                {
                    key: "continue",
                    label: translate("Continue Shopping"),
                    onClick: continueClickHandler,
                    buttonStyles: styles.continueButton,
                    linkStyles: styles.continueLink,
                    isSticky: true,
                    field: fields.showContinueShopping,
                    testSelector: "continueBtn",
                },
                {
                    key: "addToList",
                    label: translate("Add to List"),
                    onClick: addToListHandler,
                    buttonStyles: styles.addToListButton,
                    linkStyles: styles.addToListLink,
                    isSticky: true,
                    testSelector: "addToListBtn",
                    field: fields.showAddToList,
                    isDisabled: !canAddAllToList,
                    isVisible: Boolean(wishListSettings && cartState?.cartLines),
                },
            ]}
        />
    );
};

const widgetModule: WidgetModule = {
    component: OnePageCheckoutConfirmationActions,
    definition: {
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
        displayName: "Actions",
        fieldDefinitions: orderActionSetFieldDefinitions([
            { name: fields.showPrint },
            { name: fields.showContinueShopping },
            { name: fields.showAddToList },
        ]),
    },
};

export default widgetModule;
